import cx from 'classnames'
import { motion } from 'motion/react'
import { FC, useMemo } from 'react'
import {
  easeTransition,
  separatorAnimation,
  textAnimation,
} from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { Separator } from 'src/theme/styled'

import { HeroProps } from '../types'

import * as SC from './styled'

const HeroHome: FC<HeroProps> = ({
  className,
  htmlTag = 'hgroup',
  htmlTitleTag = 'h2',
  title,
  richTextProps,
  pictureProps,
  actionButtonProps,
  color = colors.blueLight,
}) => {
  const { animations } = useHeroHomeMotion()

  return (
    <SC.Container
      className={cx('HeroHome', className)}
      as={htmlTag}
      $color={color}
    >
      {pictureProps && (
        <SC._Picture
          parallaxRatio={0.2}
          {...pictureProps}
          {...animations.picture}
        />
      )}
      <SC.Wrapper>
        {title && (
          <SC.Title as={htmlTitleTag}>
            <SC.TitleContent {...animations.title}>{title}</SC.TitleContent>
          </SC.Title>
        )}
        <Separator role="separator" aria-hidden {...animations.separator} />
        <SC.Content>
          {richTextProps && (
            <SC.RichTextWrapper {...animations.richText}>
              <SC._RichText {...richTextProps} />
            </SC.RichTextWrapper>
          )}
          {actionButtonProps && (
            <motion.div {...animations.action}>
              <SC._ActionButton {...actionButtonProps} />
            </motion.div>
          )}
        </SC.Content>
      </SC.Wrapper>
      <SC.BigBG aria-hidden />
      <SC.ContentBG aria-hidden {...animations.background} />
    </SC.Container>
  )
}

const useHeroHomeMotion = () => {
  const transitions = useMemo(
    () => ({
      graphics: { ...easeTransition, duration: 1.3, delay: 2.2 },
    }),
    []
  )

  const animations = useMemo(
    () => ({
      background: {
        initial: { x: 'calc(380 / 1440 * 100vw)', y: '-35%' },
        animate: { x: 0, y: 0 },
        transition: transitions.graphics,
      },
      picture: {
        initial: { opacity: 0, scale: 1.2, x: '10%' },
        animate: { opacity: 1, scale: 1, x: 0 },
        transition: transitions.graphics,
      },
      title: textAnimation({ delay: 2.8 }),
      separator: separatorAnimation({ delay: 2.8, duration: 1 }),
      richText: textAnimation({ y: 'var(--hero_text-offset)', delay: 3 }),
      action: textAnimation({ y: 'var(--hero_text-offset)', delay: 3.15 }),
    }),
    [transitions]
  )

  return { animations }
}

export default HeroHome
